import { Bell, Check, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationItemPremium } from "./NotificationItemPremium";
import { useNativeHaptics } from "@/hooks/useNativeHaptics";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useMemo } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { formatDistanceToNow, isToday, isThisWeek, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

export const NotificationBellPremium = () => {
  const { notifications, unreadCount, markAllAsRead, loading } = useNotifications();
  const [open, setOpen] = useState(false);
  const haptics = useNativeHaptics();

  // Agrupar notificações por período
  const groupedNotifications = useMemo(() => {
    const groups: { today: typeof notifications; thisWeek: typeof notifications; earlier: typeof notifications } = {
      today: [],
      thisWeek: [],
      earlier: []
    };

    notifications.forEach(n => {
      const date = parseISO(n.created_at);
      if (isToday(date)) {
        groups.today.push(n);
      } else if (isThisWeek(date)) {
        groups.thisWeek.push(n);
      } else {
        groups.earlier.push(n);
      }
    });

    return groups;
  }, [notifications]);

  const handleOpen = () => {
    haptics.light();
    setOpen(true);
  };

  const handleMarkAllAsRead = () => {
    haptics.medium();
    markAllAsRead();
  };

  return (
    <>
      {/* Bell Button with Animated Badge */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={handleOpen}
        data-testid="notifications-button"
        aria-label={unreadCount > 0 ? `Notificações: ${unreadCount} não ${unreadCount === 1 ? 'lida' : 'lidas'}` : 'Notificações'}
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        <AnimatePresence mode="wait">
          {unreadCount > 0 && (
            <motion.span
              key={unreadCount}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 25 }}
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-gradient-to-br from-primary to-primary/80 text-[10px] font-bold text-primary-foreground flex items-center justify-center shadow-lg"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </Button>

      {/* Premium Drawer Sheet */}
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="h-[85vh] rounded-t-[24px]">
          {/* Premium Header */}
          <DrawerHeader className="border-b border-border/50 pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={() => setOpen(false)}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <DrawerTitle className="text-lg font-semibold">
                  Notificações
                </DrawerTitle>
              </div>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="h-8 text-xs text-primary hover:text-primary/80"
                >
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                  Marcar todas
                </Button>
              )}
            </div>
          </DrawerHeader>

          {/* Notifications List */}
          <ScrollArea className="flex-1 h-full">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full"
                />
              </div>
            ) : notifications.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center py-20 px-6"
              >
                <div className="w-20 h-20 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                  <Bell className="h-10 w-10 text-muted-foreground/50" />
                </div>
                <p className="text-base font-medium text-foreground mb-1">
                  Tudo em dia!
                </p>
                <p className="text-sm text-muted-foreground text-center">
                  Você não tem notificações no momento
                </p>
              </motion.div>
            ) : (
              <div className="pb-safe">
                {/* Today */}
                {groupedNotifications.today.length > 0 && (
                  <div>
                    <div className="sticky top-0 z-10 px-4 py-2 bg-background/95 backdrop-blur-sm">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Hoje
                      </p>
                    </div>
                    <motion.div
                      initial="hidden"
                      animate="visible"
                      variants={{
                        visible: { transition: { staggerChildren: 0.05 } }
                      }}
                    >
                      {groupedNotifications.today.map((notification) => (
                        <NotificationItemPremium
                          key={notification.id}
                          notification={notification}
                          onDismiss={() => setOpen(false)}
                        />
                      ))}
                    </motion.div>
                  </div>
                )}

                {/* This Week */}
                {groupedNotifications.thisWeek.length > 0 && (
                  <div>
                    <div className="sticky top-0 z-10 px-4 py-2 bg-background/95 backdrop-blur-sm">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Esta semana
                      </p>
                    </div>
                    <motion.div
                      initial="hidden"
                      animate="visible"
                      variants={{
                        visible: { transition: { staggerChildren: 0.05 } }
                      }}
                    >
                      {groupedNotifications.thisWeek.map((notification) => (
                        <NotificationItemPremium
                          key={notification.id}
                          notification={notification}
                          onDismiss={() => setOpen(false)}
                        />
                      ))}
                    </motion.div>
                  </div>
                )}

                {/* Earlier */}
                {groupedNotifications.earlier.length > 0 && (
                  <div>
                    <div className="sticky top-0 z-10 px-4 py-2 bg-background/95 backdrop-blur-sm">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Anteriores
                      </p>
                    </div>
                    <motion.div
                      initial="hidden"
                      animate="visible"
                      variants={{
                        visible: { transition: { staggerChildren: 0.05 } }
                      }}
                    >
                      {groupedNotifications.earlier.map((notification) => (
                        <NotificationItemPremium
                          key={notification.id}
                          notification={notification}
                          onDismiss={() => setOpen(false)}
                        />
                      ))}
                    </motion.div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </>
  );
};
